import React, { useEffect } from "react";
import useTreatments from "../../store/treatments/treatmentsContext";
import {
  useTable,
  useGlobalFilter,
  usePagination,
  useFilters,
  useSortBy,
} from "react-table";
import { COLORS } from "../../config";
import Checkbox from "../../components/Inputs/Checkbox";
import SelectColumnFilter from "../ReactTable/SelectColumnFilter";
import SortAscSmall from "../../components/Icons/solid/SortAscSmall";
import SortDescSmall from "../../components/Icons/solid/SortDescSmall";
import PrimaryButton from "../../components/Buttons/PrimaryButton";

function PaymentItemsTable({
  treatmentInfo,
  setTotalPaymentAmount,
  totalPaymentAmount,
  setSelectedPaymentItems,
  selectedPaymentItems,
  handlePayClick
}) {

  const {
    treatmentsState: { treatmentTransactions },
    getTreatmentTransactions,
  } = useTreatments();

  const unpaidTransactions = treatmentTransactions.unpaid;

  const addPaymentItem = (rowId) => {
    const itemToAdd = unpaidTransactions[rowId];
    setSelectedPaymentItems(prev => [...prev, itemToAdd])
    setTotalPaymentAmount(prev => {
      if (prev) return prev + Number(itemToAdd.amount);
      return Number(itemToAdd.amount);
    })
  }

  const removePaymentItem = (rowId) => {
    const itemToRemove = unpaidTransactions[rowId];
    setSelectedPaymentItems(prev => prev.filter(item => item._id !== itemToRemove._id))
    setTotalPaymentAmount(prev => prev - Number(itemToRemove.amount))
  }

  useEffect(() => {
    (async () => {
      await getTreatmentTransactions(treatmentInfo._id);
    })();
  }, []);


  
  const data = React.useMemo(
    () => [],
    
    [unpaidTransactions]
  );

  
  if (!data.length && unpaidTransactions.length) {
    data.length = 0;
    unpaidTransactions.map((transaction) => {
      return data.push({
        item: transaction.name,
        description: transaction.payment_description,
        amount: new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(transaction.amount),
      })
    });
  }
  
  const defaultColumn = React.useMemo(() => {
    return {
      Filter: SelectColumnFilter,
    };
  }, []);

  const columns = React.useMemo(
    () => [
      {
        Header: "Item",
        accessor: "item"
      },
      {
        Header: "Description",
        accessor: "description",
      },
      {
        Header: "Amount",
        accessor: "amount",
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const handleCheckboxRowClick = (rowId) => {
    const clickedItem = unpaidTransactions[rowId];
    const isSelected = selectedPaymentItems.some(item => item._id === clickedItem._id);
    if (isSelected) removePaymentItem(rowId);
    if (!isSelected) addPaymentItem(rowId);
  };
  
  return (
    <>
      <h1 className="text-2xl text-secondary mb-10">Payment items</h1>
      {unpaidTransactions.length ?
      <div className="overflow-x-scroll">
        <table
          {...getTableProps()}
          className="w-full p-2 xl:p-4 mt-4 border-4 border-transparent"
        >
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                <th className="text-left xl:px-2 pb-3 border-b-2 border-gray-100" />
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    className="text-secondary xl:px-2 pb-3 border-b-2 border-gray-100 text-left text-base font-semibold"
                  >
                    <span className="flex items-center">
                      {column.render("Header")}
                      {column.isSorted ? (
                        column.isSortedDesc ? (
                          
                          <SortAscSmall style={"ml-1 h-5 w-5"} />
                        ) : (
                          
                          <SortDescSmall style={"ml-1 h-5 w-5"} />
                        )
                      ) : (
                        ""
                      )}
                    </span>
                  </th>
                ))}
                <th className="text-left xl:px-2 pb-3 border-b-2 border-gray-100 "></th>
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()} className="">
            {page.map((row) => {
              prepareRow(row);
              const rowId = row.index;
              const isSelected = selectedPaymentItems.some(item => item._id === unpaidTransactions[rowId]._id);
              return (
                <tr
                  {...row.getRowProps()}
                  style={
                    isSelected
                      ? {
                          borderColor: `white white rgba(243, 244, 246, 1) ${COLORS.colorPrimary}`,
                        }
                      : { borderColor: "" }
                  }
                  className={`${
                    isSelected ? "border-l-4 shadow-md" : ""
                  }  border-b-2 border-gray-100`}
                >
                  <td className="text-left xl:px-2">
                    <div className="pl-4">
                      <Checkbox
                        selected={isSelected ? true : false}
                        onChange={() => handleCheckboxRowClick(row.index)}
                      />
                    </div>
                  </td>
                  {row.cells.map((cell) => {
                    if (cell.column.id === 'amount') return (
                      <td
                        {...cell.getCellProps()}
                        className="text-gray-500  xl:px-2 py-3"
                      >
                        {cell.render('Cell')}
                      </td>
                    );
                    return (
                      <td
                        {...cell.getCellProps()}
                        className="text-gray-500  xl:px-2 py-3"
                      >
                        {cell.render("Cell")}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className='flex justify-between w-full mt-8'>
          <div className="w-full">
            <div className='flex row'>
              <p className="uppercase font-bold text-secondary w-1/3">Total items</p> 
              <p className="ml-5">{selectedPaymentItems.length}</p>
            </div>
            <div className='flex row'> 
              <p className="uppercase font-bold text-secondary w-1/3">Total Amount</p> 
              <p className="ml-5">{new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(totalPaymentAmount)}</p>
            </div>
          </div>
          <PrimaryButton disabled={!selectedPaymentItems.length} text="Pay" large onClick={handlePayClick} />
        </div>
      </div>
      :
      <span>
        There are no items to be paid for this treatment.
      </span>
      }
    </>
  )
}

export default PaymentItemsTable
